import { Header, Sidebar } from 'components/Global';
import { FORMDATA } from 'constants/Data';
import { usePanelMode } from 'contexts/PanelModeContext';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { isCombosEnabled } from 'utils/featureFlags';
import {
  getDefaultRouteForPanel,
  getPanelModeFromPathname,
  getPathForMenuKey,
  getRoutePanelAffinity,
  isPathAllowedInPanel,
  pathnameToMenuKey,
  type PanelMode,
} from 'utils/panelRoutes';
import { checkPermission } from 'utils/utils';
import styles from './index.module.scss';

const GlobalLayoutInner = () => {
  const { medicinePanelMenuItems, groceryPanelMenuItems } = FORMDATA;
  const { panelMode, setPanelMode } = usePanelMode();
  const { pathname } = useLocation();
  const routePanelMode = getPanelModeFromPathname(pathname);
  const activePanelMode: PanelMode = routePanelMode ?? panelMode;
  const [finalMenuItems, setMenuItems] = useState<any>(medicinePanelMenuItems);
  const [isLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  const [selectedSidebar, setSelectedSidebar] = useState<string>('dashboard');

  const fetchedUserPermission = useSelector(
    (state: any) => state?.userProfile?.data?.permissions,
  );

  const isDashboardPermission = checkPermission(fetchedUserPermission, {
    permissionName: 'Dashboard',
    permission: ['view', 'viewAll'],
  });

  const isMedicinesPermission = checkPermission(fetchedUserPermission, {
    permissionName: 'Medicines',
    permission: ['view', 'viewAll'],
  });

  const isComboItemsPermission = checkPermission(fetchedUserPermission, {
    permissionName: 'ComboItems',
    permission: ['view', 'viewAll'],
  });

  const isGroceryItemsPermission = checkPermission(fetchedUserPermission, {
    permissionName: 'GroceryItems',
    permission: ['view', 'viewAll'],
  });

  const isCategoriesPermission = checkPermission(fetchedUserPermission, {
    permissionName: 'Categories',
    permission: ['viewAll'],
  });

  const isSubCategoriesPermission = checkPermission(fetchedUserPermission, {
    permissionName: 'SubCategories',
    permission: ['viewAll'],
  });

  const isQualityCheckPermission = checkPermission(fetchedUserPermission, {
    permissionName: 'QualityCheck',
    permission: ['view', 'viewAll'],
  });

  const isComboQualityCheckPermission = checkPermission(fetchedUserPermission, {
    permissionName: 'ComboQualityCheck',
    permission: ['view', 'viewAll'],
  });

  const isWarningTagsPermission = checkPermission(fetchedUserPermission, {
    permissionName: 'WarningTags',
    permission: ['viewAll'],
  });

  const isDoseFormsPermission = checkPermission(fetchedUserPermission, {
    permissionName: 'DoseForm',
    permission: ['viewAll'],
  });

  const isCategoryDoseFormMappingPermission = checkPermission(
    fetchedUserPermission,
    {
      permissionName: 'CategoryDoseFormMapping',
      permission: ['viewAll'],
    },
  );

  const isRejectedPermission = checkPermission(fetchedUserPermission, {
    permissionName: 'Rejected',
    permission: ['view', 'viewAll'],
  });

  const isDraftMedicinesRequestPermission = checkPermission(
    fetchedUserPermission,
    {
      permissionName: 'DraftMedicinesRequest',
      permission: ['view', 'viewAll'],
    },
  );

  const permissionMap: Record<string, boolean> = useMemo(
    () => ({
      dashboard:
        activePanelMode === 'grocery'
          ? isDashboardPermission || isGroceryItemsPermission
          : isDashboardPermission,
      medicines: isMedicinesPermission,
      combos: isCombosEnabled() && isComboItemsPermission,
      groceries: isGroceryItemsPermission,
      'grocery-quality-check': checkPermission(fetchedUserPermission, {
        permissionName: 'GroceryQualityCheck',
        permission: ['view', 'viewAll'],
      }),
      'grocery-rejected': checkPermission(fetchedUserPermission, {
        permissionName: 'GroceryRejected',
        permission: ['view', 'viewAll'],
      }),
      'grocery-categories': checkPermission(fetchedUserPermission, {
        permissionName: 'GroceryCategories',
        permission: ['view', 'viewAll'],
      }),
      'grocery-sub-categories':
        checkPermission(fetchedUserPermission, {
          permissionName: 'GroceryCategories',
          permission: ['view', 'viewAll'],
        }) &&
        checkPermission(fetchedUserPermission, {
          permissionName: 'GrocerySubCategories',
          permission: ['view', 'viewAll'],
        }),
      'draft-medicines-request': isDraftMedicinesRequestPermission,
      rejected: isRejectedPermission,
      categories: isCategoriesPermission,
      'sub-categories': isCategoriesPermission && isSubCategoriesPermission,
      'quality-check':
        isQualityCheckPermission ||
        (isCombosEnabled() && isComboQualityCheckPermission),
      'warning-tags': isWarningTagsPermission,
      'dose-form': isDoseFormsPermission,
      'category-dose-form-mapping': isCategoryDoseFormMappingPermission,
    }),
    [
      activePanelMode,
      fetchedUserPermission,
      isDashboardPermission,
      isMedicinesPermission,
      isComboItemsPermission,
      isGroceryItemsPermission,
      isDraftMedicinesRequestPermission,
      isRejectedPermission,
      isCategoriesPermission,
      isSubCategoriesPermission,
      isQualityCheckPermission,
      isComboQualityCheckPermission,
      isWarningTagsPermission,
      isDoseFormsPermission,
      isCategoryDoseFormMappingPermission,
    ],
  );

  const baseMenuItems =
    activePanelMode === 'grocery' ? groceryPanelMenuItems : medicinePanelMenuItems;

  useEffect(() => {
    if (!Array.isArray(baseMenuItems)) return;

    const updatedMenuItems = baseMenuItems.filter((item) => {
      if (item.key === 'combos' && !isCombosEnabled()) {
        return false;
      }
      return permissionMap[item.key] !== false;
    });

    setMenuItems((prev: any) => {
      if (JSON.stringify(prev) !== JSON.stringify(updatedMenuItems)) {
        return updatedMenuItems;
      }
      return prev;
    });
  }, [baseMenuItems, permissionMap]);

  // Redirect when permissions change and the current panel route is no longer allowed.
  useEffect(() => {
    if (!fetchedUserPermission) return;

    const menuKey = pathnameToMenuKey(pathname);
    if (menuKey in permissionMap && permissionMap[menuKey] === false) {
      navigate('/access-denied', { replace: true });
    }
  }, [fetchedUserPermission, pathname, permissionMap, navigate]);

  // Sync context from URL; redirect only on shared/unknown paths when panel mismatches.
  useLayoutEffect(() => {
    const affinity = getRoutePanelAffinity(pathname);

    if (affinity === 'grocery' || affinity === 'medicine') {
      if (panelMode !== affinity) {
        setPanelMode(affinity);
      }
      return;
    }

    if (!isPathAllowedInPanel(pathname, activePanelMode)) {
      navigate(getDefaultRouteForPanel(activePanelMode), { replace: true });
    }
  }, [pathname, panelMode, activePanelMode, navigate, setPanelMode]);

  const handleSidebar = (sidebarProps: any) => {
    const key = sidebarProps?.key as string;
    setSelectedSidebar(key);
    navigate(getPathForMenuKey(key));
  };

  useEffect(() => {
    const pathKey = pathnameToMenuKey(pathname);
    setSelectedSidebar(pathKey);
  }, [pathname]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
      navigate('/login');
    }
  }, [navigate]);

  return !isLoading ? (
    isLoggedIn ? (
      <div className={styles['global-layout']}>
        <div className={styles['global-layout__wrapper']}>
          <div className={styles['global-layout__header']}>
            <div className={styles['global-layout__body']}>
              <div className={styles['global-layout__body--sidebar']}>
                <Sidebar
                  key={`${activePanelMode}-${finalMenuItems?.length}`}
                  items={finalMenuItems}
                  selectedSidebar={selectedSidebar}
                  handleSidebar={handleSidebar}
                />
              </div>
              <div className={styles['global-layout__body--body']}>
                <Header />
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : null
  ) : null;
};

export default GlobalLayoutInner;
